import { Link as RouterLink } from "react-router-dom";
import { useSelector } from "react-redux";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import AppleIcon from "@mui/icons-material/Apple";
import AndroidIcon from "@mui/icons-material/Android";
import BuildIcon from "@mui/icons-material/Inventory2Outlined";
import EditIcon from "@mui/icons-material/EditOutlined";

const statusChipProps = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "active") return { color: "success", variant: "filled" };
    if (s === "inactive") return { color: "default", variant: "outlined" };
    if (s === "requested") return { color: "warning", variant: "filled" };
    return { color: "default", variant: "outlined" };
};

const Apps = (props) => {
    const {
        id,
        name,
        logo,
        description,
        isAndriod,
        isIos,
        app_status,
        requestedBy,
    } = props.app;

    const from = props.from;
    const _organizationId = props?.organizationId;
    const organization = useSelector((s) => s.organizationDetails.organization);
    const organizationRoles = organization?.user_role;
    const role = useSelector((s) => s.userLogin.userInfo.data.role);

    const isRequested = app_status === "Requested";
    const isSuperAdmin = role === "Super_Admin";
    const isOrgAdmin = role !== "Super_Admin" && organizationRoles === "Admin";

    // Builds page route — Super_Admin always goes via the organization-scoped
    // URL; other roles use the plain tenant route.
    const buildsHref = isSuperAdmin
        ? `/organization/${_organizationId}/app/${id}/builds`
        : `/app/${id}/builds`;

    return (
        <TableRow hover>
            <TableCell sx={{ fontWeight: 600 }}>{name}</TableCell>
            <TableCell sx={{ width: 72 }}>
                <Avatar
                    src={logo}
                    alt={name}
                    variant="rounded"
                    sx={{ width: 44, height: 44, border: "1px solid", borderColor: "divider" }}
                />
            </TableCell>
            <TableCell sx={{ color: "text.secondary" }}>{description}</TableCell>
            <TableCell>
                <Stack direction="row" spacing={0.5}>
                    {isIos === "1" || isIos === 1 ? (
                        <Chip icon={<AppleIcon />} label="iOS" size="small" variant="outlined" />
                    ) : null}
                    {isAndriod === "1" || isAndriod === 1 ? (
                        <Chip icon={<AndroidIcon />} label="Android" size="small" variant="outlined" />
                    ) : null}
                </Stack>
            </TableCell>

            {/* Requested-by column for Super_Admin on requested apps */}
            {isRequested && isSuperAdmin && (
                <TableCell sx={{ color: "text.secondary" }}>{requestedBy}</TableCell>
            )}

            {/* Builds link for the "Apps" tab (non-super-admin). For requested
                apps this column is replaced by the requested-by cell above. */}
            {!isRequested && from === "Apps" && !isSuperAdmin && (
                <TableCell align="center">
                    <IconButton component={RouterLink} to={buildsHref} title="Builds">
                        <BuildIcon />
                    </IconButton>
                </TableCell>
            )}

            {/* Super_Admin sees status + builds on non-requested apps */}
            {isSuperAdmin && !isRequested && (
                <>
                    <TableCell>
                        <Chip
                            size="small"
                            label={app_status}
                            {...statusChipProps(app_status)}
                        />
                    </TableCell>
                    <TableCell align="center">
                        <IconButton component={RouterLink} to={buildsHref} title="Builds">
                            <BuildIcon />
                        </IconButton>
                    </TableCell>
                </>
            )}

            {/* Org admin on non-requested apps: status + edit */}
            {isOrgAdmin && !isRequested && (
                <>
                    <TableCell align="center">
                        <Chip
                            size="small"
                            label={app_status}
                            {...statusChipProps(app_status)}
                        />
                    </TableCell>
                    <TableCell align="center">
                        <IconButton component={RouterLink} to={`/app/edit/${id}`} title="Edit app">
                            <EditIcon />
                        </IconButton>
                    </TableCell>
                </>
            )}

            {/* Org admin on requested apps: requested-by + edit */}
            {isOrgAdmin && isRequested && (
                <>
                    <TableCell sx={{ color: "text.secondary" }}>{requestedBy}</TableCell>
                    <TableCell align="center">
                        <IconButton component={RouterLink} to={`/app/edit/${id}`} title="Edit requested app">
                            <EditIcon />
                        </IconButton>
                    </TableCell>
                </>
            )}
        </TableRow>
    );
};

export default Apps;
